/**
 * Admin Sylt · Section-Editor (Phase 3B).
 * Liest alle `sylt.*`-Section-Keys aus site_content, analog zu AdminStartseite.
 * Reihenfolge entspricht dem tatsächlichen Rendering auf `/sylt`.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import {
    Save, Check, ExternalLink, AlertCircle,
    Layers, Sparkles, BookOpen, Globe, LayoutGrid, Anchor, Quote,
    Image as ImageIcon, SplitSquareHorizontal, ListOrdered, Package, Mail,
} from "lucide-react";
import { api } from "@/lib/auth";
import type { MediaRef } from "@/lib/media";
import { invalidateSiteContentCache } from "@/lib/siteContent";
import {
    SectionCard, TextField, TextareaField, ToggleField,
    MediaField, MediaListField, RepeaterField,
} from "@/components/admin/editor/fields";
import MediaPicker, { type PickerContext } from "@/components/admin/MediaPicker";
import TabNavigation, { type Tab } from "@/components/admin/TabNavigation";
import SectionEnabledToggle from "@/components/admin/SectionEnabledToggle";
import RichTextField from "@/components/admin/RichTextField";

const ALL_TAB_ID = "all";

/** Icon-Mapping pro Sylt-Sektion (Tab-ID = Key-Suffix). Lucide 14 px Standard. */
const TAB_ICONS: Record<string, ReactElement> = {
    hero: <Sparkles size={14} />,
    prolog: <BookOpen size={14} />,
    viewer: <Globe size={14} />,
    use_cases: <LayoutGrid size={14} />,
    why: <Anchor size={14} />,
    reference: <Quote size={14} />,
    before_after: <SplitSquareHorizontal size={14} />,
    process: <ListOrdered size={14} />,
    pakete: <Package size={14} />,
    gallery: <ImageIcon size={14} />,
    contact: <Mail size={14} />,
};

const SYLT_KEYS = [
    "sylt.hero", "sylt.prolog", "sylt.viewer", "sylt.use_cases",
    "sylt.why", "sylt.reference", "sylt.before_after",
    "sylt.process", "sylt.pakete", "sylt.gallery", "sylt.contact",
] as const;

type SyltSections = Record<string, Record<string, unknown>>;

const META: Record<string, { title: string; anchor: string; description: string }> = {
    "sylt.hero": { title: "Hero", anchor: "hero", description: "Insel-Hero am Seitenanfang. Der Renderer wählt automatisch: Foto → Bild, Video → autoplay-loop stumm, 360°-Medium → mit Badge." },
    "sylt.prolog": { title: "Prolog · Die Idee", anchor: "prolog", description: "Editorial-Einführung: Ein Reetdach altert. Nur Text." },
    "sylt.viewer": { title: "360°-Viewer", anchor: "viewer", description: "Zeige bis zu vier 360°-Videos vom Sylter Refugium als Kachel-Galerie. Bei einem Video Solo-Modus, ab zwei Videos Grid-Layout mit Modal-Öffnung beim Klick. Aktiviere/deaktiviere einzelne Videos ohne sie zu löschen." },
    "sylt.use_cases": { title: "Einsatz auf der Insel", anchor: "use-cases", description: "Kachel-Raster für Reetdach, Familienhaus, Boutique-Hotel etc. Jede Kachel unterstützt alle Medientypen." },
    "sylt.why": { title: "Warum HypeFly auf Sylt", anchor: "why", description: "Vier Vertrauensblöcke: Anreise, Wetterfenster, Diskretion — nur Text + Icon." },
    "sylt.reference": { title: "Referenz · Kampen-Beispiel", anchor: "reference", description: "Editorial-Referenz mit drei Frames, Zitat und Autor. Frames sind Bilder (kein Video)." },
    "sylt.before_after": { title: "Vorher · Nachher", anchor: "compare", description: "Bild-Slider zum direkten Vergleich. Beide Bilder sollten dieselbe Perspektive und Auflösung haben. Nur Fotos." },
    "sylt.process": { title: "Ablauf auf der Insel", anchor: "process", description: "Fünf Schritte: Vorgespräch bis digitale Übergabe — nur Text." },
    "sylt.pakete": { title: "Pakete für Sylt", anchor: "pakete", description: "Refugium · Anwesen · Erbe — drei Pakete, nur Text." },
    "sylt.gallery": { title: "Insel-Galerie", anchor: "gallery", description: "Editorial-Impressionen. Aktuell nicht auf /sylt gerendert (Phase 4)." },
    "sylt.contact": { title: "Kontakt", anchor: "kontakt", description: "Sylt-spezifische E-Mail und Anschrift." },
};

function deepEqual(a: unknown, b: unknown): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
}

export default function AdminSyltPage() {
    const [saved, setSaved] = useState<SyltSections>({});
    const [edit, setEdit] = useState<SyltSections>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [flash, setFlash] = useState<"ok" | "err" | null>(null);
    const [flashMsg, setFlashMsg] = useState("");
    const [pickerCtx, setPickerCtx] = useState<PickerContext>(null);

    /* -------- Tabs: „Alle" als erster + eine Sektion pro Tab.
     *  Tab-ID = Key-Suffix (unique). -------- */
    const tabIdFor = (k: string): string => k.split(".").pop() ?? k;
    const tabs = useMemo<Tab[]>(() => {
        const sectionTabs: Tab[] = SYLT_KEYS.map((k) => {
            const id = tabIdFor(k);
            return { id, label: META[k].title, icon: TAB_ICONS[id] };
        });
        return [
            { id: ALL_TAB_ID, label: "Alle", icon: <Layers size={15} /> },
            ...sectionTabs,
        ];
    }, []);
    const firstTabId = tabs[0]?.id ?? ALL_TAB_ID;
    const readHash = (): string | null => {
        if (typeof window === "undefined") return null;
        const h = window.location.hash.replace(/^#/, "");
        return h && tabs.some((t) => t.id === h) ? h : null;
    };
    const [activeTab, setActiveTab] = useState<string>(() => readHash() ?? firstTabId);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const desired = `#${activeTab}`;
        if (window.location.hash !== desired) {
            window.history.replaceState(null, "", desired);
        }
    }, [activeTab]);
    useEffect(() => {
        const onHash = () => {
            if (typeof window === "undefined") return;
            const h = window.location.hash.replace(/^#/, "");
            if (h && tabs.some((t) => t.id === h) && h !== activeTab) {
                setActiveTab(h);
            }
        };
        window.addEventListener("hashchange", onHash);
        return () => window.removeEventListener("hashchange", onHash);
    }, [activeTab, tabs]);

    useEffect(() => {
        void (async () => {
            const res = await api<{ items: Record<string, unknown> }>(
                "/api/site-content?prefix=sylt.",
            );
            const items = res.ok && res.data ? res.data.items : {};
            const sections: SyltSections = {};
            for (const k of SYLT_KEYS) {
                const v = items[k];
                sections[k] = (v && typeof v === "object" && !Array.isArray(v))
                    ? (v as Record<string, unknown>) : {};
            }
            setSaved(sections);
            setEdit(JSON.parse(JSON.stringify(sections)));
            setLoading(false);
        })();
    }, []);

    const changedKeys = SYLT_KEYS.filter((k) => !deepEqual(saved[k], edit[k]));
    const hasChanges = changedKeys.length > 0;

    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (!hasChanges) return;
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [hasChanges]);

    const setSection = useCallback((key: string, value: Record<string, unknown>) => {
        setEdit((prev) => ({ ...prev, [key]: value }));
    }, []);

    const doSave = async () => {
        if (!hasChanges) return;
        setSaving(true);
        setFlash(null);
        const errors: string[] = [];
        const nextSaved: SyltSections = { ...saved };
        for (const k of changedKeys) {
            const res = await api(`/api/admin/site-content/${encodeURIComponent(k)}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ value: edit[k] }),
            });
            if (res.ok) {
                nextSaved[k] = JSON.parse(JSON.stringify(edit[k]));
            } else {
                errors.push(`${k}: ${res.error || "unknown"}`);
            }
        }
        setSaved(nextSaved);
        setSaving(false);
        // Cache invalidieren, damit nächster Sylt-Reload sofort frische Werte sieht
        invalidateSiteContentCache();
        if (errors.length) {
            setFlash("err");
            setFlashMsg(`Fehler bei ${errors.length}/${changedKeys.length}: ${errors.join(" · ")}`);
        } else {
            setFlash("ok");
            setFlashMsg(`Gespeichert (${changedKeys.length} ${changedKeys.length === 1 ? "Section" : "Sections"})`);
            setTimeout(() => setFlash(null), 2000);
        }
    };

    const doDiscard = () => {
        if (!hasChanges) return;
        if (!confirm("Alle ungespeicherten Änderungen verwerfen?")) return;
        setEdit(JSON.parse(JSON.stringify(saved)));
    };

    const openPicker = useCallback((currentType?: string | string[], onPick?: (id: string, p: string) => void) => {
        if (!onPick) return;
        setPickerCtx({ type: currentType, onPick });
    }, []);

    if (loading) return <div className="text-sm text-hf-muted">Lade…</div>;

    /* -------- Dirty-Marker pro Tab (id = Key-Suffix). -------- */
    const dirtyTabIds = new Set<string>();
    for (const k of SYLT_KEYS) {
        if (!deepEqual(saved[k], edit[k])) dirtyTabIds.add(tabIdFor(k));
    }

    /* -------- Aktiven Section-Key aus activeTab ableiten. -------- */
    const activeKey = SYLT_KEYS.find((k) => tabIdFor(k) === activeTab) ?? SYLT_KEYS[0];
    const activeMeta = META[activeKey];

    return (
        <div data-testid="admin-sylt">
            {/* Sticky Header: Save-Bar + Tab-Leiste in einem Sticky-Container. */}
            <div
                className="sticky top-0 z-40 -mx-6 md:-mx-8 mb-8 backdrop-blur"
                style={{ background: "hsl(var(--hf-bg) / 0.94)" }}
            >
                <div
                    className="px-6 md:px-8 py-4 flex items-center justify-between"
                    style={{ borderBottom: "1px solid hsl(var(--hf-line))" }}
                >
                    <div>
                        <div className="hf-eyebrow" style={{ fontSize: 10 }}>Admin › Sylt</div>
                        <h1 className="font-display text-2xl md:text-3xl leading-tight mt-0.5">Sylt bearbeiten</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <a
                            href="/sylt" target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs md:text-sm px-3 py-2 rounded-sm border"
                            style={{ borderColor: "hsl(var(--hf-line))", background: "hsl(var(--hf-surface))" }}
                            data-testid="editor-preview-link"
                        >
                            <ExternalLink size={14} /> Live-Vorschau
                        </a>
                        <button
                            type="button" onClick={doDiscard} disabled={!hasChanges || saving}
                            data-testid="editor-discard-button"
                            className="text-xs md:text-sm px-3 py-2 rounded-sm border disabled:opacity-30"
                            style={{ borderColor: "hsl(var(--hf-line))" }}
                        >Verwerfen</button>
                        <button
                            type="button" onClick={doSave} disabled={!hasChanges || saving}
                            data-testid="editor-save-button"
                            className="inline-flex items-center gap-2 text-xs md:text-sm px-4 py-2 rounded-sm font-medium disabled:opacity-40"
                            style={{ background: hasChanges ? "hsl(var(--hf-gold))" : "hsl(var(--hf-line))", color: "hsl(var(--hf-ink))" }}
                        >
                            {flash === "ok" ? <Check size={14} /> : <Save size={14} />}
                            <span>{saving ? "Speichere…" : hasChanges ? `Speichern · ${changedKeys.length}` : "Gespeichert"}</span>
                        </button>
                    </div>
                </div>
                <TabNavigation
                    tabs={tabs}
                    activeId={activeTab}
                    onChange={setActiveTab}
                    dirtyIds={dirtyTabIds}
                    testId="admin-sylt-tabs"
                    ariaLabel="Sylt-Sektionen"
                    allTabId={ALL_TAB_ID}
                />
            </div>

            {flash === "err" && (
                <div className="mb-6 p-4 rounded-sm text-sm flex items-start gap-2"
                    style={{ background: "hsl(var(--hf-danger) / 0.12)", color: "hsl(var(--hf-danger))" }}>
                    <AlertCircle size={16} className="mt-0.5" /> {flashMsg}
                </div>
            )}

            {/* Tab-Panel: „Alle" → alle Sylt-Sektionen untereinander mit
             *  Sichtbarkeits-Übersicht oben. Sonst → nur die aktive Sektion.
             *  Zentraler Edit-State hält alle Sections. Alle Toggles (Overview
             *  + Panel-Header) mutieren dasselbe `edit`-Objekt und sind daher
             *  bidirektional synchron. */}
            <div
                role="tabpanel"
                id={`tabpanel-${activeTab}`}
                aria-labelledby={`tab-${activeTab}`}
                className="space-y-6"
            >
                {activeTab === ALL_TAB_ID ? (
                    <>
                        <section
                            className="p-5 md:p-6 rounded-md"
                            style={{
                                background: "hsl(var(--hf-surface))",
                                border: "1px solid hsl(var(--hf-line))",
                                boxShadow: "var(--shadow-warm-sm)",
                            }}
                            data-testid="all-sections-overview"
                        >
                            <div className="flex items-baseline justify-between mb-3">
                                <div>
                                    <div className="hf-eyebrow" style={{ fontSize: 10 }}>Übersicht</div>
                                    <h2 className="font-display text-xl md:text-2xl leading-tight mt-0.5">Alle Sektionen · Sichtbarkeit</h2>
                                </div>
                                <span
                                    className="text-[11px]"
                                    style={{ color: "hsl(var(--hf-muted))" }}
                                    data-testid="all-sections-overview-hint"
                                >
                                    {SYLT_KEYS.filter((k) => (edit[k] as { enabled?: boolean })?.enabled === false).length} von {SYLT_KEYS.length} ausgeblendet
                                </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {SYLT_KEYS.map((k) => (
                                    <SectionEnabledToggle
                                        key={k}
                                        enabled={(edit[k] as { enabled?: boolean })?.enabled}
                                        onChange={(next) => setSection(k, { ...edit[k], enabled: next })}
                                        label={META[k].title}
                                        testId={`overview-toggle-${tabIdFor(k)}`}
                                    />
                                ))}
                            </div>
                        </section>
                        {SYLT_KEYS.map((k) => (
                            <SectionCard
                                key={k}
                                sectionKey={k}
                                title={META[k].title}
                                description={META[k].description}
                                anchor={META[k].anchor}
                                dirty={!deepEqual(saved[k], edit[k])}
                            >
                                <SectionEnabledToggle
                                    enabled={(edit[k] as { enabled?: boolean })?.enabled}
                                    onChange={(next) => setSection(k, { ...edit[k], enabled: next })}
                                    testId={`panel-toggle-${tabIdFor(k)}`}
                                    hint={k === "sylt.hero"
                                        ? "Ohne Hero verliert die Sylt-Seite den primären Blickfang – nur ausschalten wenn absichtlich Landing-Variante ohne Hero gewünscht."
                                        : undefined}
                                />
                                <SectionEditor
                                    sectionKey={k}
                                    value={edit[k]}
                                    onChange={(v) => setSection(k, v)}
                                    openPicker={openPicker}
                                />
                            </SectionCard>
                        ))}
                    </>
                ) : (
                    <SectionCard
                        key={activeKey}
                        sectionKey={activeKey}
                        title={activeMeta.title}
                        description={activeMeta.description}
                        anchor={activeMeta.anchor}
                        dirty={!deepEqual(saved[activeKey], edit[activeKey])}
                    >
                        <SectionEnabledToggle
                            enabled={(edit[activeKey] as { enabled?: boolean })?.enabled}
                            onChange={(next) => setSection(activeKey, { ...edit[activeKey], enabled: next })}
                            testId={`panel-toggle-${tabIdFor(activeKey)}`}
                            hint={activeKey === "sylt.hero"
                                ? "Ohne Hero verliert die Sylt-Seite den primären Blickfang – nur ausschalten wenn absichtlich Landing-Variante ohne Hero gewünscht."
                                : undefined}
                        />
                        <SectionEditor
                            sectionKey={activeKey}
                            value={edit[activeKey]}
                            onChange={(v) => setSection(activeKey, v)}
                            openPicker={openPicker}
                        />
                    </SectionCard>
                )}
            </div>

            <MediaPicker ctx={pickerCtx} onClose={() => setPickerCtx(null)} />
        </div>
    );
}

/* -------- Section-Editor Router (Sylt) -------- */
function SectionEditor({
    sectionKey, value, onChange, openPicker,
}: {
    sectionKey: string;
    value: Record<string, unknown>;
    onChange: (v: Record<string, unknown>) => void;
    openPicker: (t?: string | string[], cb?: (id: string, p: string) => void) => void;
}) {
    const set = (k: string, v: unknown) => onChange({ ...value, [k]: v });
    const s = value as Record<string, unknown>;
    const getStr = (k: string) => (typeof s[k] === "string" ? (s[k] as string) : "");
    const getArr = <T,>(k: string): T[] => (Array.isArray(s[k]) ? (s[k] as T[]) : []);
    const getMedia = (k: string) => (s[k] as MediaRef);

    switch (sectionKey) {
        case "sylt.hero": {
            type HeroSlide = { id?: string; media?: MediaRef; label?: string; enabled?: boolean };
            const slides = getArr<HeroSlide>("items");
            const legacyBg = getMedia("background");
            const hasLegacy = !!(legacyBg && (
                (typeof legacyBg === "object" && (legacyBg.media_id || legacyBg.path))
                || (typeof legacyBg === "string" && legacyBg.length)
            ));
            const adoptLegacy = () => {
                const nextItems: HeroSlide[] = [
                    { id: `sh${Date.now()}`, media: legacyBg, enabled: true },
                    ...slides,
                ];
                onChange({ ...value, items: nextItems, background: null });
            };
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} testId="sylt-hero-eyebrow" />
                    <TextareaField label="Hauptüberschrift" value={getStr("headline")} onChange={(v) => set("headline", v)} testId="sylt-hero-headline" rows={2} />
                    <TextField label="Subline" value={getStr("subline")} onChange={(v) => set("subline", v)} />
                    <TextField label="Trust-Line" value={getStr("trust_line")} onChange={(v) => set("trust_line", v)} helpText="z.B. 'Anreise inklusive · Wetterfenster · Diskret seit 2004'" />
                    {hasLegacy && slides.length === 0 && (
                        <div
                            className="p-3 rounded-sm text-[12px] flex items-center justify-between gap-3"
                            style={{
                                background: "hsl(var(--hf-gold-soft))",
                                border: "1px solid hsl(var(--hf-gold-dark) / 0.4)",
                                color: "hsl(var(--hf-gold-dark))",
                            }}
                            data-testid="sylt-hero-legacy-migrate-banner"
                        >
                            <span>
                                Altes Einzel-Medium erkannt. Übernehmen, um es als ersten Slide im
                                Multi-Item-Hero zu nutzen — das alte Feld wird geleert.
                            </span>
                            <button
                                type="button"
                                onClick={adoptLegacy}
                                className="text-[11px] px-3 py-1.5 rounded-sm uppercase tracking-[0.14em] font-medium"
                                style={{
                                    background: "hsl(var(--hf-gold))",
                                    color: "hsl(var(--hf-ink))",
                                    border: "1px solid hsl(var(--hf-gold-dark))",
                                }}
                                data-testid="sylt-hero-legacy-migrate-btn"
                            >
                                Als Slide übernehmen
                            </button>
                        </div>
                    )}
                    <RepeaterField<HeroSlide>
                        label="Hero-Slides (Multi-Item)"
                        items={slides}
                        onChange={(v) => set("items", v)}
                        newItem={() => ({ id: `sh${Date.now()}`, media: null, label: "", enabled: true })}
                        testId="sylt-hero-items"
                        renderItem={(it, i, upd) => (
                            <div className="space-y-3">
                                <MediaField
                                    label={`Slide #${i + 1} · Medium`}
                                    mediaRef={it.media as MediaRef}
                                    onChange={(v) => upd({ ...it, media: v })}
                                    openPicker={openPicker}
                                    mediaType={["photo", "photo_360", "video", "video_360"]}
                                    testId={`sylt-hero-item-${i}-media`}
                                    helpText="Zulässig: Foto · 360°-Foto · Video · 360°-Video · Type wird automatisch erkannt und passend gerendert."
                                />
                                <TextField
                                    label="Untertitel (optional)"
                                    value={it.label || ""}
                                    onChange={(v) => upd({ ...it, label: v })}
                                    testId={`sylt-hero-item-${i}-label`}
                                    placeholder="z.B. Kampen · Reetdach"
                                />
                            </div>
                        )}
                    />
                </>
            );
        }
        case "sylt.prolog":
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} />
                    <TextField label="Zeile A" value={getStr("headline_a")} onChange={(v) => set("headline_a", v)} testId="sylt-prolog-a" />
                    <TextField label="Zeile B" value={getStr("headline_b")} onChange={(v) => set("headline_b", v)} testId="sylt-prolog-b" />
                    <TextField label="Zeile C" value={getStr("headline_c")} onChange={(v) => set("headline_c", v)} testId="sylt-prolog-c" />
                    <RichTextField label="Body" value={getStr("body")} onChange={(v) => set("body", v)} rows={5} testId="sylt-prolog-body" />
                </>
            );
        case "sylt.viewer": {
            type SyltItem = { id?: string; title?: string; enabled?: boolean; video?: unknown; poster?: unknown };
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} />
                    <TextField label="Zeile A" value={getStr("headline_a")} onChange={(v) => set("headline_a", v)} testId="sylt-viewer-a" />
                    <TextField label="Zeile B" value={getStr("headline_b")} onChange={(v) => set("headline_b", v)} testId="sylt-viewer-b" />
                    <RichTextField label="Body" value={getStr("body")} onChange={(v) => set("body", v)} rows={3} testId="sylt-viewer-body" />
                    <TextField label="Demo-Badge (kleiner Hinweis unter dem Player)" value={getStr("demo_badge")} onChange={(v) => set("demo_badge", v)} />
                    <RepeaterField<SyltItem>
                        label="360°-Videos (max 4)"
                        items={getArr<SyltItem>("items")}
                        onChange={(v) => set("items", v)}
                        newItem={() => ({ id: `s${Date.now()}`, title: "Neues Video", enabled: true, video: null, poster: null })}
                        minItems={0}
                        maxItems={4}
                        testId="sylt-viewer-items"
                        renderItem={(it, i, upd) => (
                            <div className="space-y-3">
                                <TextField label="Titel" value={it.title || ""} onChange={(v) => upd({ ...it, title: v })} testId={`sylt-viewer-item-${i}-title`} />
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <MediaField
                                        label="360°-Video"
                                        mediaRef={it.video as MediaRef}
                                        onChange={(v) => upd({ ...it, video: v })}
                                        openPicker={openPicker}
                                        mediaType="video_360"
                                        testId={`sylt-viewer-item-${i}-video`}
                                        helpText="Equirektangular 2:1"
                                    />
                                    <MediaField
                                        label="Poster"
                                        mediaRef={it.poster as MediaRef}
                                        onChange={(v) => upd({ ...it, poster: v })}
                                        openPicker={openPicker}
                                        mediaType={["photo", "photo_360"]}
                                        testId={`sylt-viewer-item-${i}-poster`}
                                    />
                                </div>
                            </div>
                        )}
                    />
                </>
            );
        }
        case "sylt.use_cases": {
            type Tile = { title: string; body?: string; image?: MediaRef; alt?: string; iconKey?: string };
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} />
                    <TextField label="Headline" value={getStr("headline")} onChange={(v) => set("headline", v)} testId="sylt-uc-headline" />
                    <RepeaterField<Tile>
                        label="Kacheln"
                        items={getArr<Tile>("tiles")}
                        onChange={(v) => set("tiles", v)}
                        newItem={() => ({ title: "Neue Kachel", body: "", image: null })}
                        testId="sylt-uc-tiles"
                        renderItem={(it, i, upd) => (
                            <div className="space-y-3">
                                <TextField label="Titel" value={it.title} onChange={(v) => upd({ ...it, title: v })} testId={`sylt-uc-${i}-title`} />
                                <RichTextField label="Beschreibung" value={it.body || ""} onChange={(v) => upd({ ...it, body: v })} rows={3} testId={`sylt-uc-${i}-body`} />
                                <TextField label="Alt-Text" value={it.alt || ""} onChange={(v) => upd({ ...it, alt: v })} />
                                <MediaField label="Medium" mediaRef={it.image} onChange={(v) => upd({ ...it, image: v || undefined })} openPicker={openPicker} mediaType={["photo", "photo_360", "video", "video_360"]} testId={`sylt-uc-${i}-image`} />
                            </div>
                        )}
                    />
                </>
            );
        }
        case "sylt.why": {
            type Block = { title: string; body?: string; iconKey?: string };
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} />
                    <TextField label="Headline (optional)" value={getStr("headline")} onChange={(v) => set("headline", v)} />
                    <RepeaterField<Block>
                        label="Vertrauensblöcke"
                        items={getArr<Block>("blocks")}
                        onChange={(v) => set("blocks", v)}
                        newItem={() => ({ title: "Neuer Block", body: "", iconKey: "map-pin" })}
                        testId="sylt-why-blocks"
                        renderItem={(it, i, upd) => (
                            <div className="space-y-3">
                                <TextField label="Titel" value={it.title} onChange={(v) => upd({ ...it, title: v })} testId={`sylt-why-${i}-title`} />
                                <RichTextField label="Body" value={it.body || ""} onChange={(v) => upd({ ...it, body: v })} rows={3} testId={`sylt-why-${i}-body`} />
                                <TextField label="Icon-Key" value={it.iconKey || ""} onChange={(v) => upd({ ...it, iconKey: v })} helpText="camera · clock · waves · map-pin · anchor · mountain · home" />
                            </div>
                        )}
                    />
                </>
            );
        }
        case "sylt.reference": {
            type Frame = { label?: string; image?: MediaRef };
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} />
                    <TextField label="Zeile A" value={getStr("headline_a")} onChange={(v) => set("headline_a", v)} testId="sylt-ref-a" />
                    <TextField label="Zeile B" value={getStr("headline_b")} onChange={(v) => set("headline_b", v)} testId="sylt-ref-b" />
                    <RichTextField label="Body" value={getStr("body")} onChange={(v) => set("body", v)} rows={4} testId="sylt-ref-body" />
                    <RepeaterField<Frame>
                        label="Referenz-Frames"
                        items={getArr<Frame>("frames")}
                        onChange={(v) => set("frames", v)}
                        newItem={() => ({ label: "360° Foto" })}
                        maxItems={4}
                        testId="sylt-ref-frames"
                        renderItem={(it, i, upd) => (
                            <div className="space-y-3">
                                <TextField label="Label" value={it.label || ""} onChange={(v) => upd({ ...it, label: v })} />
                                <MediaField label="Bild" mediaRef={it.image} onChange={(v) => upd({ ...it, image: v || undefined })} openPicker={openPicker} mediaType={["photo", "photo_360"]} testId={`sylt-ref-${i}-image`} />
                            </div>
                        )}
                    />
                    <TextField label="Zitat" value={getStr("quote")} onChange={(v) => set("quote", v)} />
                    <TextField label="Zitat-Autor" value={getStr("author")} onChange={(v) => set("author", v)} />
                </>
            );
        }
        case "sylt.before_after": {
            type Pair = { id?: string; title?: string; vorher?: MediaRef; nachher?: MediaRef };
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} />
                    <TextField label="Headline" value={getStr("headline")} onChange={(v) => set("headline", v)} testId="sylt-ba-headline" />
                    <RichTextField label="Lead" value={getStr("lead")} onChange={(v) => set("lead", v)} rows={2} testId="sylt-ba-lead" />
                    <TextField label="Fußnote" value={getStr("footnote")} onChange={(v) => set("footnote", v)} />
                    <RepeaterField<Pair>
                        label="Vorher/Nachher-Paare"
                        items={getArr<Pair>("pairs")}
                        onChange={(v) => set("pairs", v)}
                        newItem={() => ({ id: `pair-${Date.now()}`, title: "Neues Paar" })}
                        testId="sylt-ba-pairs"
                        renderItem={(it, i, upd) => (
                            <div className="space-y-3">
                                <TextField label="Label" value={it.title || ""} onChange={(v) => upd({ ...it, title: v })} testId={`sylt-ba-${i}-title`} />
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <MediaField label="Vorher" mediaRef={it.vorher} onChange={(v) => upd({ ...it, vorher: v || undefined })} openPicker={openPicker} mediaType="photo" testId={`sylt-ba-${i}-vorher`} />
                                    <MediaField label="Nachher" mediaRef={it.nachher} onChange={(v) => upd({ ...it, nachher: v || undefined })} openPicker={openPicker} mediaType="photo" testId={`sylt-ba-${i}-nachher`} />
                                </div>
                            </div>
                        )}
                    />
                </>
            );
        }
        case "sylt.process": {
            type Step = { n: string; title: string; body?: string };
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} />
                    <TextField label="Headline" value={getStr("headline")} onChange={(v) => set("headline", v)} />
                    <RepeaterField<Step>
                        label="Schritte"
                        items={getArr<Step>("steps")}
                        onChange={(v) => set("steps", v)}
                        newItem={() => ({ n: "06", title: "Neuer Schritt", body: "" })}
                        maxItems={5}
                        testId="sylt-process-steps"
                        renderItem={(it, i, upd) => (
                            <div className="space-y-3">
                                <div className="grid grid-cols-4 gap-3">
                                    <TextField label="Nummer" value={it.n} onChange={(v) => upd({ ...it, n: v })} />
                                    <div className="col-span-3">
                                        <TextField label="Titel" value={it.title} onChange={(v) => upd({ ...it, title: v })} testId={`sylt-process-${i}-title`} />
                                    </div>
                                </div>
                                <TextareaField label="Beschreibung" value={it.body || ""} onChange={(v) => upd({ ...it, body: v })} rows={2} />
                            </div>
                        )}
                    />
                </>
            );
        }
        case "sylt.pakete": {
            type Pkg = { name: string; line?: string; body?: string; featured?: boolean };
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} />
                    <TextField label="Headline (optional)" value={getStr("headline")} onChange={(v) => set("headline", v)} />
                    <RepeaterField<Pkg>
                        label="Pakete"
                        items={getArr<Pkg>("items")}
                        onChange={(v) => set("items", v)}
                        newItem={() => ({ name: "Neues Paket", line: "", body: "", featured: false })}
                        testId="sylt-pakete-items"
                        renderItem={(it, i, upd) => (
                            <div className="space-y-3">
                                <TextField label="Name" value={it.name} onChange={(v) => upd({ ...it, name: v })} testId={`sylt-pkg-${i}-name`} />
                                <TextField label="Untertitel/Line" value={it.line || ""} onChange={(v) => upd({ ...it, line: v })} />
                                <TextareaField label="Body" value={it.body || ""} onChange={(v) => upd({ ...it, body: v })} rows={2} />
                                <ToggleField label="Empfohlen (hervorgehoben)" value={!!it.featured} onChange={(v) => upd({ ...it, featured: v })} />
                            </div>
                        )}
                    />
                </>
            );
        }
        case "sylt.gallery": {
            type Tile = { image?: MediaRef; label?: string; enabled?: boolean };
            const legacyTiles = getArr<Tile>("tiles");
            const entries = legacyTiles.map((t) => ({
                ref: (t.image ?? null) as MediaRef,
                enabled: t.enabled !== false,
            }));
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} />
                    <TextField label="Headline" value={getStr("headline")} onChange={(v) => set("headline", v)} />
                    <MediaListField
                        label="Galerie-Medien"
                        items={entries}
                        onChange={(next) => set(
                            "tiles",
                            next.map((e, i) => ({
                                // Bestehendes `label` beibehalten, wenn Index noch existiert
                                ...(legacyTiles[i]?.label ? { label: legacyTiles[i]!.label } : {}),
                                image: e.ref,
                                enabled: e.enabled !== false,
                            })),
                        )}
                        openPicker={openPicker}
                        mediaType={["photo", "photo_360", "video", "video_360"]}
                        withVisibility
                        helpText="Zulässig: Foto · 360°-Foto · Video · 360°-Video. Type wird automatisch erkannt und im Frontend passend gerendert. Per Sichtbarkeits-Toggle können Kacheln temporär verborgen werden, ohne sie zu löschen."
                        testId="sylt-gallery-list"
                    />
                </>
            );
        }
        case "sylt.contact":
            return (
                <>
                    <TextField label="Eyebrow" value={getStr("eyebrow")} onChange={(v) => set("eyebrow", v)} />
                    <TextField label="Zeile A" value={getStr("headline_a")} onChange={(v) => set("headline_a", v)} testId="sylt-contact-a" />
                    <TextField label="Zeile B" value={getStr("headline_b")} onChange={(v) => set("headline_b", v)} testId="sylt-contact-b" />
                    <TextareaField label="Body" value={getStr("body")} onChange={(v) => set("body", v)} rows={3} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <TextField label="E-Mail" value={getStr("email")} onChange={(v) => set("email", v)} testId="sylt-contact-email" />
                        <TextField label="Telefon (optional)" value={getStr("phone")} onChange={(v) => set("phone", v)} />
                    </div>
                    <TextField label="Anschrift-Platzhalter" value={getStr("address_placeholder")} onChange={(v) => set("address_placeholder", v)} />
                    <TextField label="WhatsApp-URL (optional)" value={getStr("whatsapp_url")} onChange={(v) => set("whatsapp_url", v)} />
                </>
            );
        default:
            return <div className="text-sm text-hf-muted">Unbekannte Section.</div>;
    }
}

